import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { SVGLoader } from 'three/examples/jsm/loaders/SVGLoader.js'

/**
 * Three.js 3D 预览组件
 * - 自动最大适配视口
 * - resetKey 重置相机
 * - 最大轮廓为外框，其余为孔洞（修复未合并卫星轴重叠）
 * - 换色 / 切换背景不重建场景，只改材质与背景，保证流畅
 */
export default function ThreePreview({
  svgContent,
  thickness = 1.5,
  color = '#4f46e5',
  resetKey = 0,
  lightBg = true,
}) {
  const containerRef = useRef(null)
  const cameraRef = useRef(null)
  const controlsRef = useRef(null)
  const defaultCamPosRef = useRef(null)
  const defaultTargetRef = useRef(null)

  // 场景相关引用：换色时直接改，不销毁重建
  const sceneRef = useRef(null)
  const materialRef = useRef(null)
  const groupRef = useRef(null)
  const lightsRef = useRef({ ambient: null, dir1: null, dir2: null })
  const rendererRef = useRef(null)
  const animIdRef = useRef(null)

  // ---------- 仅在 svg / 厚度变化时重建几何 ----------
  useEffect(() => {
    const container = containerRef.current
    if (!container || !svgContent) return

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    if (thickness <= 0) {
      console.warn('thickness 必须大于 0')
      return
    }

    // 清理旧内容
    while (container.firstChild) {
      container.removeChild(container.firstChild)
    }
    if (animIdRef.current) {
      cancelAnimationFrame(animIdRef.current)
      animIdRef.current = null
    }

    const scene = new THREE.Scene()
    scene.background = new THREE.Color(lightBg ? 0xf0f2f5 : 0x1a1a2e)
    sceneRef.current = scene

    const camera = new THREE.PerspectiveCamera(40, width / height, 0.1, 100000)
    cameraRef.current = camera

    const ambient = new THREE.AmbientLight(0xffffff, lightBg ? 0.75 : 0.6)
    const dir1 = new THREE.DirectionalLight(0xffffff, lightBg ? 0.7 : 0.9)
    dir1.position.set(1, 1.2, 1.5).normalize().multiplyScalar(500)
    const dir2 = new THREE.DirectionalLight(0xffffff, lightBg ? 0.3 : 0.35)
    dir2.position.set(-1, -0.4, -0.8).normalize().multiplyScalar(400)
    scene.add(ambient, dir1, dir2)
    lightsRef.current = { ambient, dir1, dir2 }

    const group = new THREE.Group()
    groupRef.current = group

    // 材质单独持有，换色时只改 color
    const material = new THREE.MeshStandardMaterial({
      color: new THREE.Color(color),
      metalness: 0.15,
      roughness: 0.55,
      side: THREE.DoubleSide,
      flatShading: false,
    })
    materialRef.current = material

    try {
      const loader = new SVGLoader()
      const svgData = loader.parse(svgContent)
      const curveSegments = 24

      const shapeEntries = []
      const shapeArea = (shape) => {
        const pts = shape.getPoints(curveSegments)
        if (!pts || pts.length < 3) return 0
        let a = 0
        for (let i = 0; i < pts.length; i++) {
          const j = (i + 1) % pts.length
          a += pts[i].x * pts[j].y
          a -= pts[j].x * pts[i].y
        }
        return Math.abs(a) / 2
      }

      for (const path of svgData.paths) {
        let shapes
        try {
          shapes = SVGLoader.createShapes(path)
        } catch (err) {
          console.warn('跳过无法解析的 path:', err)
          continue
        }
        for (const shape of shapes) {
          const area = shapeArea(shape)
          if (area < 1e-4) continue
          shapeEntries.push({ shape, area })
        }
      }

      if (shapeEntries.length === 0) {
        camera.position.set(0, 80, 160)
        camera.lookAt(0, 0, 0)
        defaultCamPosRef.current = camera.position.clone()
        defaultTargetRef.current = new THREE.Vector3(0, 0, 0)
      } else {
        shapeEntries.sort((a, b) => b.area - a.area)

        const outer = shapeEntries[0].shape
        const outerClone = outer.clone()
        outerClone.holes = []

        for (let i = 1; i < shapeEntries.length; i++) {
          const holeShape = shapeEntries[i].shape
          const holePath = new THREE.Path()
          const pts = holeShape.getPoints(curveSegments)
          if (pts.length < 3) continue
          holePath.moveTo(pts[0].x, pts[0].y)
          for (let p = 1; p < pts.length; p++) {
            holePath.lineTo(pts[p].x, pts[p].y)
          }
          holePath.closePath()
          outerClone.holes.push(holePath)
          if (holeShape.holes && holeShape.holes.length > 0) {
            for (const h of holeShape.holes) outerClone.holes.push(h)
          }
        }
        if (outer.holes && outer.holes.length > 0) {
          for (const h of outer.holes) outerClone.holes.push(h)
        }

        let geometry
        try {
          geometry = new THREE.ExtrudeGeometry(outerClone, {
            depth: thickness,
            bevelEnabled: false,
            curveSegments,
            steps: 1,
          })
        } catch (err) {
          console.warn('整体拉伸失败，回退为逐形状拉伸:', err)
          for (const { shape, area } of shapeEntries) {
            if (area < 1e-4) continue
            try {
              let g = new THREE.ExtrudeGeometry(shape, {
                depth: thickness,
                bevelEnabled: false,
                curveSegments,
                steps: 1,
              })
              g = g.toNonIndexed()
              g.computeVertexNormals()
              group.add(new THREE.Mesh(g, material))
            } catch (e2) {
              console.warn('单形状拉伸失败:', e2)
            }
          }
          geometry = null
        }

        if (geometry) {
          geometry = geometry.toNonIndexed()
          geometry.computeVertexNormals()
          group.add(new THREE.Mesh(geometry, material))
        }

        group.scale.y = -1

        const box = new THREE.Box3().setFromObject(group)
        const size = box.getSize(new THREE.Vector3())
        const maxDim = Math.max(size.x, size.y, size.z) || 1
        const normScale = 120 / maxDim
        group.scale.set(normScale, -normScale, normScale)

        const box2 = new THREE.Box3().setFromObject(group)
        const center = box2.getCenter(new THREE.Vector3())
        group.position.sub(center)
        scene.add(group)

        const finalBox = new THREE.Box3().setFromObject(group)
        const sphere = new THREE.Sphere()
        finalBox.getBoundingSphere(sphere)
        const radius = Math.max(sphere.radius, 1)

        const vFovRad = (camera.fov * Math.PI) / 180
        const hFovRad = 2 * Math.atan(Math.tan(vFovRad / 2) * camera.aspect)
        const distByH = radius / Math.sin(vFovRad / 2)
        const distByW = radius / Math.sin(hFovRad / 2)
        const dist = Math.max(distByH, distByW) * 1.08

        const camPos = new THREE.Vector3(dist * 0.55, dist * 0.42, dist * 0.78)
        camera.position.copy(camPos)
        camera.lookAt(0, 0, 0)
        camera.near = Math.max(0.01, dist / 500)
        camera.far = dist * 20
        camera.updateProjectionMatrix()

        defaultCamPosRef.current = camPos.clone()
        defaultTargetRef.current = new THREE.Vector3(0, 0, 0)
      }
    } catch (e) {
      console.error('3D 模型生成失败:', e)
      camera.position.set(0, 80, 160)
      camera.lookAt(0, 0, 0)
      defaultCamPosRef.current = camera.position.clone()
      defaultTargetRef.current = new THREE.Vector3(0, 0, 0)
    }

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false })
    renderer.setSize(width, height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    container.appendChild(renderer.domElement)
    rendererRef.current = renderer

    const controls = new OrbitControls(camera, renderer.domElement)
    controls.enableDamping = true
    controls.dampingFactor = 0.08
    controls.minDistance = 5
    controls.maxDistance = 5000
    controls.target.set(0, 0, 0)
    controls.update()
    controlsRef.current = controls

    const animate = () => {
      animIdRef.current = requestAnimationFrame(animate)
      controls.update()
      renderer.render(scene, camera)
    }
    animate()

    const onResize = () => {
      const w = container.clientWidth
      const h = container.clientHeight
      if (w === 0 || h === 0) return
      camera.aspect = w / h
      camera.updateProjectionMatrix()
      renderer.setSize(w, h)

      if (defaultCamPosRef.current && group.children.length > 0) {
        const finalBox = new THREE.Box3().setFromObject(group)
        const sphere = new THREE.Sphere()
        finalBox.getBoundingSphere(sphere)
        const radius = Math.max(sphere.radius, 1)
        const vFovRad = (camera.fov * Math.PI) / 180
        const hFovRad = 2 * Math.atan(Math.tan(vFovRad / 2) * camera.aspect)
        const distByH = radius / Math.sin(vFovRad / 2)
        const distByW = radius / Math.sin(hFovRad / 2)
        const dist = Math.max(distByH, distByW) * 1.08
        defaultCamPosRef.current = new THREE.Vector3(
          dist * 0.55,
          dist * 0.42,
          dist * 0.78
        )
      }
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(animIdRef.current)
      window.removeEventListener('resize', onResize)
      controls.dispose()
      renderer.dispose()
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose()
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose())
          else obj.material.dispose()
        }
      })
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement)
      }
      cameraRef.current = null
      controlsRef.current = null
      sceneRef.current = null
      materialRef.current = null
      groupRef.current = null
      rendererRef.current = null
      lightsRef.current = { ambient: null, dir1: null, dir2: null }
    }
    // 注意：color / lightBg 不在依赖里，换色与换背景不触发重建
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svgContent, thickness])

  // ---------- 换色：只改材质，不重建 ----------
  useEffect(() => {
    if (materialRef.current) {
      materialRef.current.color.set(color)
      materialRef.current.needsUpdate = true
    }
  }, [color])

  // ---------- 切换背景：只改场景背景与灯光强度 ----------
  useEffect(() => {
    const scene = sceneRef.current
    if (!scene) return
    scene.background = new THREE.Color(lightBg ? 0xf0f2f5 : 0x1a1a2e)
    const { ambient, dir1, dir2 } = lightsRef.current
    if (ambient) ambient.intensity = lightBg ? 0.75 : 0.6
    if (dir1) dir1.intensity = lightBg ? 0.7 : 0.9
    if (dir2) dir2.intensity = lightBg ? 0.3 : 0.35
  }, [lightBg])

  // ---------- 重置视角 ----------
  useEffect(() => {
    if (!resetKey) return
    const camera = cameraRef.current
    const controls = controlsRef.current
    const pos = defaultCamPosRef.current
    const target = defaultTargetRef.current
    if (!camera || !controls || !pos || !target) return
    camera.position.copy(pos)
    controls.target.copy(target)
    controls.update()
  }, [resetKey])

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
    />
  )
}
